"use client";

import { createContext, useContext, useMemo, useState } from "react";
import { packageKeys, type PackageKey } from "@/lib/brand";

/** The package cards, in display order: the offerings plus Custom. */
export const PACKAGE_KEYS = packageKeys;
export type { PackageKey };

/** Care & support billing choice — "none" means the add-on isn't included. */
export type CareBilling = "none" | "monthly" | "annual";

/** The billing period of the Care add-on, independent of whether it's added. */
export type CarePeriod = "monthly" | "annual";

interface PricingSelection {
  /** The currently chosen package (a card is always selected). */
  readonly pkg: PackageKey;
  selectPackage: (key: PackageKey) => void;
  /**
   * The Care & support add-on choice; "none" until added. Derived from
   * {@link careIncluded} + {@link carePeriod} so downstream consumers (the
   * deep-link, the start form) read a single value.
   */
  readonly care: CareBilling;
  /** The selected billing period, shown whether or not the add-on is added. */
  readonly carePeriod: CarePeriod;
  setCarePeriod: (period: CarePeriod) => void;
  /** Whether the Care add-on is part of the selection. */
  readonly careIncluded: boolean;
  /** Add or remove the Care add-on at the current billing period. */
  toggleCare: () => void;
}

const PricingSelectionContext = createContext<PricingSelection | null>(null);

/**
 * Holds the package + Care selection for the pricing workflow. Wraps both the
 * pricing section (where the choice is made) and the start form (which reads it
 * back), so selecting a card is the beginning of one continuous flow rather than
 * a jump straight into a generic form. Defaults: first package, no Care add-on.
 */
export function PricingSelectionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [pkg, setPkg] = useState<PackageKey>(PACKAGE_KEYS[0]);
  // Annual is the recommended (best-value) period, so it's the default shown.
  const [carePeriod, setCarePeriod] = useState<CarePeriod>("annual");
  const [careIncluded, setCareIncluded] = useState(false);

  const value = useMemo<PricingSelection>(
    () => ({
      pkg,
      selectPackage: setPkg,
      care: careIncluded ? carePeriod : "none",
      carePeriod,
      setCarePeriod,
      careIncluded,
      toggleCare: () => setCareIncluded((added) => !added),
    }),
    [pkg, carePeriod, careIncluded],
  );

  return (
    <PricingSelectionContext.Provider value={value}>
      {children}
    </PricingSelectionContext.Provider>
  );
}

/** Read the pricing selection. Throws if used outside the provider. */
export function usePricingSelection(): PricingSelection {
  const ctx = useContext(PricingSelectionContext);
  if (!ctx) {
    throw new Error(
      "usePricingSelection must be used within a PricingSelectionProvider",
    );
  }
  return ctx;
}
